import { useEffect } from "react";
import React, { useState } from "react";
import {
  Table,
  Row,
  Button,
  Input,
  Col,
  Spinner,
  CardFooter,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import DebouncedSearch from "components/shared/DebouncedSearch";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { deleteAddOns } from "store/actions/addOnsAction";
import { getAddOns } from "store/actions/addOnsAction";
import PaginationComponent from "components/shared/Pagination";
import { toast } from "react-toastify";
import usePermissions from "hooks/usePermissions";
import AddOnsModal from "./AddOnsModal";

const AddOns = () => {
  const dispatch = useDispatch();
  const { allAddOns, addOnsRest } = useSelector((state) => state.addOns);
  const { user } = useSelector((state) => state.auth);
  const [hitsPerPage, setHitsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [addOnsModal, setAddOnsModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [activeAddOns, setActiveAddOns] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSearch = (debouncedValue) => {
    setDebouncedSearch(debouncedValue);
  };
  const {
    add: addAccess,
    edit: editAccess,
    delete: deleteAccess,
    get: getAccess,
  } = usePermissions();

  const addOnsModalToggler = () => {
    if (addOnsModal) {
      setActiveAddOns(null);
      setEditMode(false);
    }
    setAddOnsModal(!addOnsModal);
  };

  const confirmationModalToggler = () => {
    if (confirmationModal) {
      setActiveAddOns(null);
    }
    setConfirmationModal(!confirmationModal);
  };

  const initiateEditAddOns = (addOns) => {
    setActiveAddOns(addOns);
    setEditMode(true);
    addOnsModalToggler();
  };
  const initiateDeleteAddOns = (addOns) => {
    setActiveAddOns(addOns);
    confirmationModalToggler();
  };

  useEffect(() => {
    setIsFetching(true);
    dispatch(
      getAddOns(user?.restaurantID, debouncedSearch, hitsPerPage, currentPage)
    ).then(() => setIsFetching(false));
  }, [debouncedSearch, hitsPerPage, currentPage, dispatch, user?.restaurantID]);

  const handleDeleteAddOns = () => {
    setIsDeleting(true);
    dispatch(
      deleteAddOns(activeAddOns?.id, activeAddOns?.image || "", () => {
        setTimeout(() => {
          dispatch(getAddOns(user?.restaurantID, "", 5, 0));
          confirmationModalToggler();
          setIsDeleting(false);
          toast.success("Add On Deleted Successfully");
        }, 3000);
      })
    );
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  return (
    <>
      <Row className="justify-content-end px-4 py-3">
        <Col sm="5" lg="4">
          <DebouncedSearch
            placeholder="Search Add On by name"
            onSearch={handleSearch}
          />
        </Col>
        <Col sm="4" lg="3" className="mt-3 mt-sm-0">
          <Input
            type="select"
            id="exampleCustomSelect"
            name="customSelect"
            value={hitsPerPage}
            onChange={(e) => {
              setHitsPerPage(e.target.value);
              setCurrentPage(0);
            }}
          >
            <option value="5">Show Entries</option>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </Input>
        </Col>
        {addAccess && (
          <Col sm="3" lg="2" className="mt-3 mt-sm-0">
            <Button color="primary" onClick={addOnsModalToggler} block>
              Add
            </Button>
          </Col>
        )}
      </Row>
      <Table className="align-items-center table-flush" responsive>
        <thead className="thead-light">
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Description</th>
            <th scope="col">Price</th>
            <th scope="col">Image</th>
            {editAccess && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {isFetching ? (
            <tr>
              <td colSpan="5" className="text-center py-5">
                <Spinner color="primary" />
              </td>
            </tr>
          ) : getAccess && allAddOns?.length > 0 ? (
            allAddOns?.map((addOns) => (
              <tr key={addOns?.id}>
                <th className="text-capitalize">{addOns?.name}</th>
                <td style={{ maxWidth: "100px" }}>
                  {addOns?.description ? (
                    <div className="text-truncate">{addOns?.description}</div>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  <div className="text-truncate">{`$${addOns?.price}`}</div>
                </td>
                <td>
                  {addOns?.image ? (
                    <img
                      src={addOns?.image}
                      alt="Label"
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  ) : (
                    "N/A"
                  )}
                </td>
                {(editAccess || deleteAccess) && (
                  <td>
                    {editAccess && (
                      <Button
                        className="btn-sm"
                        color="primary"
                        onClick={() => initiateEditAddOns(addOns)}
                      >
                        Edit
                      </Button>
                    )}
                    {deleteAccess && (
                      <Button
                        className="btn-sm"
                        color="danger"
                        onClick={() => initiateDeleteAddOns(addOns)}
                      >
                        Delete
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center py-5">
                No Add Ons
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <CardFooter className="py-4">
        <PaginationComponent
          currentPage={currentPage}
          totalPages={addOnsRest?.nbPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </CardFooter>
      <AddOnsModal
        isOpen={addOnsModal}
        toggle={addOnsModalToggler}
        editMode={editMode}
        activeAddOns={activeAddOns}
      />
      <ConfirmationModal
        title="Delete Label"
        text={
          <>
            <h3>Are you sure you want to delete this Add On?</h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              Deleting Add Ons may cause some issues if they are added in any of
              the items.
            </span>
          </>
        }
        isOpen={confirmationModal}
        toggle={confirmationModalToggler}
        isLoading={isDeleting}
        onConfirmation={handleDeleteAddOns}
      />
    </>
  );
};

export default AddOns;
